/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import { CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea, CButton, CSpinner, CAlert } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const SupplierTypeInputs = ({ onSubmit, onCancel, isLoading = false, submitResponse = null, initialData = null, isEdit = false }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    supplierTypeName: '',
    supplierTypeDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        supplierTypeName: initialData.SupplierTypeName || '',
        supplierTypeDescription: initialData.SupplierTypeDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) {
        setFormData({ supplierTypeName: '', supplierTypeDescription: '' });
      }
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.supplierTypeName.trim()) newErrors.supplierTypeName = t('supplierTypeNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: t('userSessionNotFound') });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      SupplierTypeName: formData.supplierTypeName.trim(),
      SupplierTypeDescription: formData.supplierTypeDescription.trim()
    };

    if (isEdit && initialData?.SupplierTypeId) {
      payload.SupplierTypeId = initialData.SupplierTypeId;
    }

    onSubmit(payload);
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ supplierTypeName: '', supplierTypeDescription: '' });
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    if (!isEdit) {
      return t('supplierTypeCreated');
    } else {
      return t('supplierTypeUpdated');
    }
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse?.Result && submitResponse.Result.ErrorCode !== 0 && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription || t('somethingWentWrong')}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="supplierTypeName" style={{ fontStyle: 'normal' }}>
              {t('supplierTypeName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="supplierTypeName"
              name="supplierTypeName"
              value={formData.supplierTypeName}
              onChange={handleInputChange}
              invalid={!!errors.supplierTypeName}
              disabled={isLoading}
              placeholder={t('enterSupplierTypeName')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.supplierTypeName && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.supplierTypeName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="supplierTypeDescription" style={{ fontStyle: 'normal' }}>
              {t('Description')}
            </CFormLabel>
            <CFormTextarea
              id="supplierTypeDescription"
              name="supplierTypeDescription"
              rows={4}
              value={formData.supplierTypeDescription}
              onChange={handleInputChange}
              invalid={!!errors.supplierTypeDescription}
              disabled={isLoading}
              placeholder={t('enterSupplierTypeDescription')}
              style={{ fontStyle: 'normal' }}
            />
            {errors.supplierTypeDescription && (
              <div className="invalid-feedback d-block" style={{ fontStyle: 'normal' }}>
                {errors.supplierTypeDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading} style={{ fontStyle: 'normal' }}>
              {isLoading
                ? (<><CSpinner size="sm" className="me-2" /> {t('saving')}</>)
                : (<><CIcon icon={cilSave} className="me-2" /> {t('save')}</>)}
            </CButton>
            <CButton 
              color="secondary" 
              variant="outline" 
              onClick={handleCancel} 
              disabled={isLoading}
              style={{ fontStyle: 'normal' }}
            >
              <CIcon icon={cilX} className="me-2" /> {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

SupplierTypeInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default SupplierTypeInputs;


